import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { coreBusinessList } from '@/views/CoreBusiness/coreBusiness'
import { newsList } from '@/views/NewsContent/newsList'
import type { Component } from 'vue'

/**
 * 路由元信息类型
 */
interface RouteMeta {
  title?: string
  keepAlive?: boolean
  [key: string]: unknown
  [key: symbol]: unknown
}

/**
 * 获取类型对应的 ID 列表
 */
const getIdsByType = (list: { type: string; id: number }[], type: string): number[] => {
  return list.filter(item => item.type === type).map(item => item.id)
}

/**
 * 核心业务类型
 */
type CoreBusinessType = 'oversizedproducts' | 'cardfaction' | 'firstlegcargo' | 'overseaswarehouse'

/**
 * 物流中心路由组件映射
 */
const logisticsCenterComponents: Record<string, () => Promise<Component>> = {
  wuna: () => import('@/views/LogisticsDistributionCenter/WunaContent.vue') as Promise<Component>,
  xnw: () => import('@/views/LogisticsDistributionCenter/XnwContent.vue') as Promise<Component>,
  txs: () => import('@/views/LogisticsDistributionCenter/TxsContent.vue') as Promise<Component>,
  adn: () => import('@/views/LogisticsDistributionCenter/AdnContent.vue') as Promise<Component>,
  bmb: () => import('@/views/LogisticsDistributionCenter/BmbContent.vue') as Promise<Component>,
  cal: () => import('@/views/LogisticsDistributionCenter/CalContent.vue') as Promise<Component>,
  zgz: () => import('@/views/LogisticsDistributionCenter/ZgzContent.vue') as Promise<Component>,
  xjz: () => import('@/views/LogisticsDistributionCenter/XjzContent.vue') as Promise<Component>,
}

/**
 * 动态生成核心业务详情路由
 */
const generateCoreBusinessDetailRoutes = (): RouteRecordRaw[] => {
  const coreBusinessTypes: CoreBusinessType[] = ['oversizedproducts', 'cardfaction', 'firstlegcargo', 'overseaswarehouse']
  const routes: RouteRecordRaw[] = []

  coreBusinessTypes.forEach(type => {
    const ids = getIdsByType(coreBusinessList as unknown as { type: string; id: number }[], type)
    if (ids.length > 0) {
      routes.push({
        path: `${type}/:id`,
        name: `${type}Detail`,
        meta: { title: `核心业务详情` } as RouteMeta,
        component: () => import('@/views/CoreBusiness/CoreContentItem.vue'),
        beforeEnter: (_to) => {
          const routeName = _to.params.id as string
          if (!ids.includes(Number(routeName))) {
            return { name: 'not-found' }
          }
          return true
        },
      })
    }
  })

  return routes
}

/**
 * 动态生成新闻详情路由
 */
const generateNewsDetailRoutes = (): RouteRecordRaw[] => {
  const newsTypes = ['companynews', 'industrytrends'] as const
  const routes: RouteRecordRaw[] = []

  newsTypes.forEach(type => {
    const ids = getIdsByType(newsList as unknown as { type: string; id: number }[], type)
    if (ids.length > 0) {
      routes.push({
        path: `${type}/:id`,
        name: `${type}Detail`,
        meta: {
          title: type === 'companynews' ? '公司新闻详情' : '行业动态详情',
        } as RouteMeta,
        component: () => import('@/views/NewsContent/NewsContentItem.vue'),
        beforeEnter: (_to) => {
          const routeName = _to.params.id as string
          if (!ids.includes(Number(routeName))) {
            return { name: 'not-found' }
          }
          return true
        },
      })
    }
  })

  return routes
}

/**
 * 动态生成物流中心路由
 */
const generateLogisticsCenterRoutes = (): RouteRecordRaw[] => {
  return Object.entries(logisticsCenterComponents).map(([key, component]) => ({
    path: key,
    name: key,
    meta: { title: '物流集散中心' } as RouteMeta,
    component,
  }))
}

/**
 * 主路由配置
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeContent.vue'),
    redirect: '/index',
    children: [
      // 首页
      {
        path: 'index',
        name: 'index',
        meta: { title: '首页' } as RouteMeta,
        component: () => import('@/views/IndexContent/IndexContent.vue'),
      },

      // 关于我们
      {
        path: 'about',
        name: 'about',
        meta: { title: '关于我们' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/about/aboutindex',
        children: [
          {
            path: 'aboutindex',
            name: 'aboutIndex',
            meta: { title: '关于我们' } as RouteMeta,
            component: () => import('@/views/AboutContent/components/AboutIndex.vue'),
          },
          {
            path: 'intro',
            name: 'aboutIntro',
            meta: { title: '企业简介' } as RouteMeta,
            component: () => import('@/views/AboutContent/components/AboutIntro.vue'),
          },
          {
            path: 'culture',
            name: 'aboutCulture',
            meta: { title: '企业文化' } as RouteMeta,
            component: () => import('@/views/AboutContent/components/AboutCulture.vue'),
          },
        ],
      },

      // 核心业务
      {
        path: 'corebusiness',
        name: 'corebusiness',
        meta: { title: '核心业务' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/corebusiness/corebusinessindex',
        children: [
          {
            path: 'corebusinessindex',
            name: 'corebusinessIndex',
            meta: { title: '核心业务' } as RouteMeta,
            component: () => import('@/views/CoreBusiness/CoreBusinessIndex.vue'),
          },
          {
            path: 'oversizedproducts',
            name: 'oversizedproducts',
            meta: { title: '超大件产品尾程物流派送' } as RouteMeta,
            component: () => import('@/views/CoreBusiness/CoreBusinessIndex.vue'),
          },
          {
            path: 'cardfaction',
            name: 'cardfaction',
            meta: { title: '卡派Q&A' } as RouteMeta,
            component: () => import('@/views/CoreBusiness/CoreBusinessIndex.vue'),
          },
          {
            path: 'firstlegcargo',
            name: 'firstlegcargo',
            meta: { title: '头程货物运输' } as RouteMeta,
            component: () => import('@/views/CoreBusiness/CoreBusinessIndex.vue'),
          },
          {
            path: 'overseaswarehouse',
            name: 'overseaswarehouse',
            meta: { title: '物流一体化服务' } as RouteMeta,
            component: () => import('@/views/CoreBusiness/CoreBusinessIndex.vue'),
          },
          // 动态生成的详情路由
          ...generateCoreBusinessDetailRoutes(),
        ],
      },

      // 物流集散中心
      {
        path: 'logisticsdistributioncenter',
        name: 'logisticsdistributioncenter',
        meta: { title: '物流集散中心' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/logisticsdistributioncenter/logisticsdistributioncenterindex',
        children: [
          {
            path: 'logisticsdistributioncenterindex',
            name: 'logisticsdistributioncenterIndex',
            meta: { title: '物流集散中心' } as RouteMeta,
            component: () => import('@/views/LogisticsDistributionCenter/LogisticsDistributionIndex.vue'),
          },
          // 动态生成的物流中心路由
          ...generateLogisticsCenterRoutes(),
        ],
      },

      // 新闻动态
      {
        path: 'news',
        name: 'news',
        meta: { title: '新闻动态' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/news/newsindex',
        children: [
          {
            path: 'newsindex',
            name: 'newsIndex',
            meta: { title: '新闻动态' } as RouteMeta,
            component: () => import('@/views/NewsContent/NewsIndex.vue'),
          },
          {
            path: 'companynews',
            name: 'companynews',
            meta: { title: '公司新闻' } as RouteMeta,
            component: () => import('@/views/NewsContent/NewsIndex.vue'),
          },
          {
            path: 'industrytrends',
            name: 'industrytrends',
            meta: { title: '行业动态' } as RouteMeta,
            component: () => import('@/views/NewsContent/NewsIndex.vue'),
          },
          // 动态生成的详情路由
          ...generateNewsDetailRoutes(),
        ],
      },

      // 合作案例
      {
        path: 'cases',
        name: 'cases',
        meta: { title: '合作案例' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/cases/casesindex',
        children: [
          {
            path: 'casesindex',
            name: 'casesIndex',
            meta: { title: '合作案例' } as RouteMeta,
            component: () => import('@/views/CasesContent/CasesIndex.vue'),
          },
          {
            path: 'customer',
            name: 'customer',
            meta: { title: '合作客户' } as RouteMeta,
            component: () => import('@/views/CasesContent/CasesCustomer.vue'),
          },
        ],
      },

      // 联系我们
      {
        path: 'contactus',
        name: 'contactus',
        meta: { title: '联系我们' } as RouteMeta,
        component: () => import('@/views/AboutContent/AboutContent.vue'),
        redirect: '/contactus/contactusindex',
        children: [
          {
            path: 'contactusindex',
            name: 'contactusIndex',
            meta: { title: '联系我们' } as RouteMeta,
            component: () => import('@/views/ContactUs/ContactUsIndex.vue'),
          },
        ],
      },
    ],
  },

  // 404 页面
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    meta: { title: '页面不存在' } as RouteMeta,
    component: () => import('@/views/not-found.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// 全局前置守卫 - 更新页面标题
router.beforeEach((to) => {
  const title = to.meta.title as string | undefined
  if (title) {
    document.title = `${title} - EFFICROSS`
  }
  return true
})

// 全局后置守卫 - 滚动到顶部
router.afterEach(() => {
  window.scrollTo(0, 0)
})

export default router
